/**
 * ContactChipInput
 *
 * Recipient input with auto-complete from the contacts API.
 * Shows chips for added contacts, with a text input that searches contacts.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { api, EmailAddress } from '@/lib/api';
import { cn } from '@/lib/utils';

interface ContactChipInputProps {
  label: string;
  addresses: EmailAddress[];
  onChange: (addresses: EmailAddress[]) => void;
  accountId: string | undefined;
  className?: string;
}

interface ContactSuggestion {
  email: string;
  name: string | null;
}

export function ContactChipInput({
  label,
  addresses,
  onChange,
  accountId,
  className,
}: ContactChipInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchContacts = useCallback(
    async (query: string) => {
      if (!accountId || query.length < 2) {
        setSuggestions([]);
        return;
      }
      try {
        const result = await api.listEmailContacts({
          accountId,
          search: query,
          limit: 8,
        });
        setSuggestions(
          result.contacts.map((c) => ({
            email: c.emailAddress,
            name: c.displayName,
          })),
        );
        setShowSuggestions(true);
        setHighlightIndex(-1);
      } catch {
        setSuggestions([]);
      }
    },
    [accountId],
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => searchContacts(value), 200);
    },
    [searchContacts],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const addAddress = useCallback(
    (addr: EmailAddress) => {
      // Avoid duplicates
      if (addresses.some((a) => a.email === addr.email)) return;
      onChange([...addresses, addr]);
      setInputValue('');
      setSuggestions([]);
      setShowSuggestions(false);
      inputRef.current?.focus();
    },
    [addresses, onChange],
  );

  const removeAddress = useCallback(
    (email: string) => {
      onChange(addresses.filter((a) => a.email !== email));
    },
    [addresses, onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
          const s = suggestions[highlightIndex];
          addAddress({ email: s.email, name: s.name || undefined });
        } else if (inputValue.includes('@')) {
          addAddress({ email: inputValue.trim() });
        }
      } else if (e.key === 'Backspace' && inputValue === '' && addresses.length > 0) {
        removeAddress(addresses[addresses.length - 1].email);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    },
    [inputValue, addresses, suggestions, highlightIndex, addAddress, removeAddress],
  );

  return (
    <div className={cn('relative', className)}>
      <div className="flex items-center gap-1 flex-wrap min-h-[36px] px-2 py-1 border border-border rounded-md bg-background focus-within:ring-1 focus-within:ring-ring">
        <span className="text-xs text-muted-foreground font-medium w-8 flex-shrink-0">{label}</span>
        {addresses.map((addr) => (
          <span
            key={addr.email}
            className="inline-flex items-center gap-1 bg-muted rounded-full px-2 py-0.5 text-xs"
          >
            <span className="truncate max-w-[140px]">{addr.name || addr.email}</span>
            <button
              onClick={() => removeAddress(addr.email)}
              className="hover:bg-foreground/10 rounded-full p-0.5"
              aria-label={`Remove ${addr.email}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          onBlur={() => {
            // Delay to allow click on suggestion before committing
            setTimeout(() => {
              setShowSuggestions(false);
              // Commit typed text as address on blur
              const trimmed = inputValue.trim();
              if (trimmed) {
                if (trimmed.includes('@')) {
                  addAddress({ email: trimmed });
                } else if (trimmed.length >= 2) {
                  // Treat as a name-only entry with a placeholder email
                  addAddress({ email: trimmed, name: trimmed });
                }
              }
            }, 200);
          }}
          className="flex-1 min-w-[120px] bg-transparent text-sm outline-none"
          placeholder={addresses.length === 0 ? 'Type a name or email...' : ''}
        />
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-48 overflow-y-auto">
          {suggestions.map((s, idx) => (
            <button
              key={s.email}
              className={cn(
                'w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors',
                idx === highlightIndex && 'bg-muted',
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                addAddress({ email: s.email, name: s.name || undefined });
              }}
            >
              <div className="font-medium truncate">{s.name || s.email}</div>
              {s.name && <div className="text-xs text-muted-foreground truncate">{s.email}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
